import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Alert, Paper, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppraisalService } from '../../services/appraisal.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { ICycleDetail, ICycleRequest, IReviewSummary } from '../../interfaces/appraisal.interface';
import { cycleStatusColor } from './bands';
import { formatDate } from './dates';
import CycleDetailsForm, { cycleRequestFrom, emptyCycleRequest, validateCycleRequest } from './CycleDetailsForm';
import CriteriaPanel from './CriteriaPanel';
import ParticipantsPanel from './ParticipantsPanel';
import CycleOverview from './CycleOverview';
import ConfirmDialog from './ConfirmDialog';

type TabKey = 'overview' | 'details' | 'criteria' | 'participants';
type Confirm = 'start' | 'close' | 'delete' | null;

/**
 * One appraisal cycle. While it is a draft this is the editor (details, criteria, participants, start);
 * once started it opens on the overview, and participants stay editable until it is closed.
 */
const CycleEditor: React.FC = () => {
    const { cycleId } = useParams<{ cycleId: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation(['appraisal', 'common']);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [cycle, setCycle] = useState<ICycleDetail | null>(null);
    const [reviews, setReviews] = useState<IReviewSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<TabKey>('details');
    const [details, setDetails] = useState<ICycleRequest>(emptyCycleRequest);
    const [criteriaDirty, setCriteriaDirty] = useState(false);
    const [confirm, setConfirm] = useState<Confirm>(null);
    const [busy, setBusy] = useState(false);

    const applyCycle = (c: ICycleDetail) => {
        setCycle(c);
        setDetails(cycleRequestFrom(c));
    };

    const loadReviews = useCallback(async () => {
        if (!cycleId) return;
        try {
            setReviews(await AppraisalService.cycleReviews(institutionId, cycleId));
        } catch (err) {
            toast.showApiError(err, t('errors.loadReviews'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, cycleId]);

    /** Participant changes alter the counts on the cycle too, so both are reloaded. */
    const reload = useCallback(async () => {
        if (!cycleId) return;
        try {
            const [c] = await Promise.all([AppraisalService.getCycle(institutionId, cycleId), loadReviews()]);
            setCycle(c);
            return c;
        } catch (err) {
            toast.showApiError(err, t('editor.errors.load'));
            return undefined;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, cycleId, loadReviews]);

    useEffect(() => {
        (async () => {
            const c = await reload();
            if (c) {
                setDetails(cycleRequestFrom(c));
                setTab(c.status === 'DRAFT' ? 'details' : 'overview');
            }
            setLoading(false);
        })();
    }, [reload]);

    if (loading) {
        return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;
    }
    if (!cycle) {
        return <Box sx={{ p: 3 }}><Alert severity="error">{t('editor.errors.load')}</Alert></Box>;
    }

    const isDraft = cycle.status === 'DRAFT';
    const missingReviewer = reviews.filter(r => !r.reviewer).length;
    const startBlockers: string[] = [];
    if (isDraft) {
        if (criteriaDirty) startBlockers.push(t('editor.startBlockers.unsavedCriteria'));
        if (cycle.criteria.length === 0) startBlockers.push(t('editor.startBlockers.noCriteria'));
        else if (cycle.weightTotal !== 100) startBlockers.push(t('editor.startBlockers.weights', { total: cycle.weightTotal }));
        if (reviews.length === 0) startBlockers.push(t('editor.startBlockers.noParticipants'));
        if (missingReviewer > 0) startBlockers.push(t('editor.startBlockers.missingReviewer', { count: missingReviewer }));
    }
    const detailsDirty = JSON.stringify(details) !== JSON.stringify(cycleRequestFrom(cycle));

    const saveDetails = async () => {
        const error = validateCycleRequest(details);
        if (error) {
            toast.showError(t(`cycleForm.errors.${error}`));
            return;
        }
        setBusy(true);
        try {
            applyCycle(await AppraisalService.updateCycle(institutionId, cycle.id, { ...details, name: details.name.trim() }));
            toast.showSuccess(t('cycleForm.saved'));
        } catch (err) {
            toast.showApiError(err, t('cycleForm.errors.save'));
        } finally {
            setBusy(false);
        }
    };

    const runConfirmed = async () => {
        setBusy(true);
        try {
            if (confirm === 'start') {
                applyCycle(await AppraisalService.startCycle(institutionId, cycle.id));
                await loadReviews();
                setTab('overview');
                toast.showSuccess(t('editor.started'));
            } else if (confirm === 'close') {
                applyCycle(await AppraisalService.closeCycle(institutionId, cycle.id));
                await loadReviews();
                toast.showSuccess(t('editor.closed'));
            } else if (confirm === 'delete') {
                await AppraisalService.deleteCycle(institutionId, cycle.id);
                toast.showSuccess(t('editor.deleted'));
                navigate('/appraisals/cycles');
                return;
            }
            setConfirm(null);
        } catch (err) {
            const fallback = confirm === 'start' ? t('editor.errors.start')
                : confirm === 'close' ? t('editor.errors.close') : t('editor.errors.delete');
            toast.showApiError(err, fallback);
        } finally {
            setBusy(false);
        }
    };

    const confirmText = {
        start: { title: t('editor.confirmStart.title'), body: t('editor.confirmStart.body'), label: t('editor.start') },
        close: { title: t('editor.confirmClose.title'), body: t('editor.confirmClose.body'), label: t('editor.close') },
        delete: { title: t('editor.confirmDelete.title'), body: t('editor.confirmDelete.body', { name: cycle.name }), label: t('common:actions.delete') },
    };

    return (
        <Box sx={{ p: 3 }}>
            <Button component={RouterLink} to="/appraisals/cycles" startIcon={<ArrowBackIcon />} sx={{ mb: 1 }}>
                {t('editor.back')}
            </Button>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h5">{cycle.name}</Typography>
                            <Chip size="small" label={t(`cycleStatus.${cycle.status}`)} color={cycleStatusColor[cycle.status]} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {t('editor.period', { from: formatDate(cycle.periodStart), to: formatDate(cycle.periodEnd) })}
                            {' · '}
                            {t('editor.dues', { self: formatDate(cycle.selfReviewDue), manager: formatDate(cycle.managerReviewDue) })}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {isDraft && (
                            <>
                                <Button color="error" onClick={() => setConfirm('delete')} disabled={busy}>{t('editor.delete')}</Button>
                                <Button variant="contained" onClick={() => setConfirm('start')} disabled={busy || startBlockers.length > 0}>
                                    {t('editor.start')}
                                </Button>
                            </>
                        )}
                        {cycle.status === 'ACTIVE' && (
                            <Button variant="contained" color="warning" onClick={() => setConfirm('close')} disabled={busy}>
                                {t('editor.close')}
                            </Button>
                        )}
                    </Box>
                </Box>
                {startBlockers.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
                        {startBlockers.map(b => (
                            <Typography component="li" variant="body2" color="text.secondary" key={b}>{b}</Typography>
                        ))}
                    </Box>
                )}
                {cycle.status === 'CLOSED' && (
                    <Alert severity="info" sx={{ mt: 1 }}>{t('editor.closedNote')}</Alert>
                )}
            </Paper>

            <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} sx={{ mb: 2 }}>
                {!isDraft && <Tab value="overview" label={t('editor.tabs.overview')} />}
                <Tab value="details" label={t('editor.tabs.details')} />
                <Tab value="criteria" label={t('editor.tabs.criteria')} />
                <Tab value="participants" label={t('editor.tabs.participants')} />
            </Tabs>

            {!isDraft && (
                <Box hidden={tab !== 'overview'}>
                    <CycleOverview institutionId={institutionId} cycle={cycle} />
                </Box>
            )}
            <Box hidden={tab !== 'details'} sx={{ maxWidth: 640 }}>
                <CycleDetailsForm value={details} onChange={setDetails} disabled={!isDraft || busy} />
                {isDraft && (
                    <Box sx={{ mt: 2 }}>
                        <Button variant="contained" onClick={saveDetails} disabled={busy || !detailsDirty}>{t('common:actions.save')}</Button>
                    </Box>
                )}
            </Box>
            <Box hidden={tab !== 'criteria'}>
                <CriteriaPanel institutionId={institutionId} cycle={cycle} onSaved={applyCycle} onDirtyChange={setCriteriaDirty} />
            </Box>
            <Box hidden={tab !== 'participants'}>
                <ParticipantsPanel institutionId={institutionId} cycle={cycle} reviews={reviews} onChanged={reload} />
            </Box>

            <ConfirmDialog
                open={confirm !== null}
                title={confirm ? confirmText[confirm].title : ''}
                body={confirm ? confirmText[confirm].body : ''}
                confirmLabel={confirm ? confirmText[confirm].label : ''}
                danger={confirm === 'delete'}
                busy={busy}
                onConfirm={runConfirmed}
                onClose={() => setConfirm(null)}
            />
        </Box>
    );
};

export default CycleEditor;
